import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { uploadMemoMedia } from 'networking/mindfeed/services/media.service';
import type { FormValues } from './useFormInitialisation';

export type UploadStatus = 'uploading' | 'done' | 'error';

export type UploadItem = {
  // Stable local key for React lists.
  key: string;
  // Object URL for a freshly picked file. Existing media are shown from
  // `mediaId` instead.
  previewUrl: string;
  status: UploadStatus;
  // Present once uploaded — the id to send as imageIds/audioIds.
  mediaId?: string;
  // MIME type, handy for choosing how to render (e.g. audio players, file icons).
  mimeType?: string;
  // Original filename of a freshly-picked file (unknown for existing media).
  name?: string;
  // Existing media (edit mode) doesn't own its preview URL, so don't revoke it.
  isExisting?: boolean;
};

type MediaFieldName = 'imageIds' | 'audioIds' | 'videoIds' | 'fileIds';

/** Upload logic for a media field — the items themselves live in the form. */
export function useMediaUpload({
  name,
  boxId,
}: {
  name: MediaFieldName;
  boxId?: string;
}) {
  const { control, getValues, setValue } = useFormContext<FormValues>();
  const items = (useWatch({ control, name }) as UploadItem[] | undefined) ?? [];

  const counter = useRef(0);
  const objectUrls = useRef<Set<string>>(new Set());

  // Revoke any URL that's no longer in the field's value — covers removal,
  // and the field being reset after a submit.
  useEffect(() => {
    const currentUrls = new Set(items.map((item) => item.previewUrl));
    objectUrls.current.forEach((url) => {
      if (currentUrls.has(url)) return;
      URL.revokeObjectURL(url);
      objectUrls.current.delete(url);
    });
  }, [items]);

  // Revoke anything still outstanding if the composer is abandoned mid-upload.
  useEffect(() => {
    const urls = objectUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      if (!boxId || files.length === 0) return;

      files.forEach((file) => {
        const key = `upload-${counter.current++}`;
        const previewUrl = URL.createObjectURL(file);
        objectUrls.current.add(previewUrl);

        const current = (getValues(name) as UploadItem[] | undefined) ?? [];
        const newItem: UploadItem = {
          key,
          previewUrl,
          status: 'uploading',
          mimeType: file.type,
          name: file.name,
        };
        setValue(name, [...current, newItem], { shouldValidate: true });

        uploadMemoMedia(boxId, file)
          .then((mediaId) => {
            const latest = (getValues(name) as UploadItem[] | undefined) ?? [];
            setValue(
              name,
              latest.map((item) =>
                item.key === key ? { ...item, status: 'done', mediaId } : item,
              ),
              { shouldValidate: true },
            );
          })
          .catch(() => {
            const latest = (getValues(name) as UploadItem[] | undefined) ?? [];
            setValue(
              name,
              latest.map((item) =>
                item.key === key ? { ...item, status: 'error' } : item,
              ),
              { shouldValidate: true },
            );
          });
      });
    },
    [boxId, name, getValues, setValue],
  );

  const removeItem = useCallback(
    (key: string) => {
      const current = (getValues(name) as UploadItem[] | undefined) ?? [];
      const item = current.find((it) => it.key === key);
      if (item && !item.isExisting && objectUrls.current.has(item.previewUrl)) {
        URL.revokeObjectURL(item.previewUrl);
        objectUrls.current.delete(item.previewUrl);
      }
      setValue(
        name,
        current.filter((it) => it.key !== key),
        { shouldValidate: true },
      );
    },
    [name, getValues, setValue],
  );

  const mediaIds = items
    .filter((it) => it.status === 'done' && it.mediaId)
    .map((it) => it.mediaId as string);

  const isUploading = items.some((it) => it.status === 'uploading');

  return { items, addFiles, removeItem, mediaIds, isUploading };
}
